import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ArtistSwitcher from '@/components/ArtistSwitcher';
import ArtistProfile from '@/components/ArtistProfile';
import ArtistMusicSection, { SpotifyTrack } from '@/components/ArtistMusicSection';
import ArtistSocialMedia from '@/components/ArtistSocialMedia';
import PlaylistGrid from '@/components/PlaylistGrid';
import { getArtistData, getAllArtists } from '@/lib/artistData';

/* ── Per-artist hero color config ── */
const heroConfig: Record<string, {
  radial: string; orb1: string; orb2: string; orb3: string;
  dotColor: string; badge: string; pulseDot: string;
  textGradient: string; subtitle: string;
}> = {
  aimusicdreams: {
    radial:       'rgba(245,158,11,0.20)',
    orb1:         'bg-amber-500/25',
    orb2:         'bg-orange-500/20',
    orb3:         'bg-yellow-600/15',
    dotColor:     'rgba(245,158,11,0.75)',
    badge:        'border-amber-500/40 bg-amber-500/10 text-amber-300',
    pulseDot:     'bg-amber-400',
    textGradient: 'from-amber-400 via-orange-300 to-yellow-400',
    subtitle:     'Dansband · Satir · Raggarock · Pop · Trubadur · Country · Sverige',
  },
  idreamz: {
    radial:       'rgba(6,182,212,0.20)',
    orb1:         'bg-cyan-500/25',
    orb2:         'bg-blue-600/20',
    orb3:         'bg-teal-600/15',
    dotColor:     'rgba(6,182,212,0.75)',
    badge:        'border-cyan-500/40 bg-cyan-500/10 text-cyan-300',
    pulseDot:     'bg-cyan-400',
    textGradient: 'from-cyan-400 via-blue-300 to-teal-400',
    subtitle:     'Hardstyle · EDM · AI-genererad elektronika · 160 BPM',
  },
};

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ArtistsPage = () => {
  const [selectedArtistId, setSelectedArtistId] = useState<string>('aimusicdreams');
  const artists        = getAllArtists();
  const selectedArtist = getArtistData(selectedArtistId) || artists[0];
  const cfg            = heroConfig[selectedArtistId] ?? heroConfig['aimusicdreams'];

  useEffect(() => { window.scrollTo(0, 0); }, [selectedArtistId]);

  const handleTrackSelect = (track: SpotifyTrack) => {
    if (!track.preview_url) {
      // No 30-sec preview available — send user straight to Spotify
      window.open(track.external_urls.spotify, '_blank');
      return;
    }
    setCurrentTrack({
      id:         track.id,
      name:       track.name,
      artist:     selectedArtist.name,
      image:      track.album.images?.[0]?.url,
      previewUrl: track.preview_url,
      externalUrl: track.external_urls.spotify,
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>AI Music — AIMusicDreams & iDreamz</title>
        <meta name="description" content="AIMusicDreams — dansband och folkmusik möter AI. iDreamz — hardstyle och EDM utan gränser." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://aifinity.se/artists" />
      </Helmet>

      {/* ── Dynamic Aurora Hero ── */}
      <section className="pt-16 pb-24 bg-hero-bg relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedArtistId + '-bg'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 pointer-events-none"
            aria-hidden
          >
            <div
              className="absolute inset-0"
              style={{ background: `radial-gradient(ellipse 70% 55% at 50% -5%, ${cfg.radial}, transparent)` }}
            />
            <div className={`aurora-1 absolute top-[-20%] left-[8%]  w-[500px] h-[500px] rounded-full ${cfg.orb1} blur-[100px]`} />
            <div className={`aurora-2 absolute top-[5%]   right-[5%] w-[400px] h-[400px] rounded-full ${cfg.orb2} blur-[110px]`} />
            <div className={`aurora-3 absolute bottom-[-10%] left-[40%] w-[350px] h-[350px] rounded-full ${cfg.orb3} blur-[90px]`} />
            <div className="absolute inset-0 opacity-[0.055]"
              style={{ backgroundImage: `radial-gradient(circle, ${cfg.dotColor} 1px, transparent 1px)`, backgroundSize: '30px 30px' }} />
            <div className="absolute inset-0 opacity-[0.03]"
              style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          </motion.div>
        </AnimatePresence>

        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
            <span className={`inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-8 border ${cfg.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${cfg.pulseDot}`} />
              AI Music
            </span>
          </motion.div>

          <motion.h1
            custom={1} initial="hidden" animate="visible" variants={fadeUp}
            className="text-4xl sm:text-[56px] font-extrabold tracking-[-0.025em] leading-[1.06] mb-4"
          >
            <span className="text-white">{selectedArtist.name}</span><br />
            <span className={`bg-gradient-to-r ${cfg.textGradient} bg-clip-text text-transparent`}
              style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              AI-skapad musik
            </span>
          </motion.h1>

          <motion.p
            custom={2} initial="hidden" animate="visible" variants={fadeUp}
            className="text-sm text-white/40 max-w-sm mx-auto leading-relaxed mb-8 tracking-wide"
          >
            {cfg.subtitle}
          </motion.p>

          <motion.p
            custom={3} initial="hidden" animate="visible" variants={fadeUp}
            className="text-lg text-white/50 max-w-xl mx-auto leading-[1.7] font-light mb-10"
          >
            Musik komponerad, producerad och framförd av artificiell intelligens — i gränssnittet
            mellan algorithm och känsla.
          </motion.p>

          {/* Artist switcher */}
          <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
            <ArtistSwitcher
              currentArtist={selectedArtistId}
              onArtistChange={setSelectedArtistId}
            />
          </motion.div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />
      </section>

      {/* ── Main content ── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <ArtistProfile artist={selectedArtist} />
          <ArtistMusicSection artist={selectedArtist} onTrackSelect={handleTrackSelect} accentColor={selectedArtistId === 'idreamz' ? '#06b6d4' : '#f59e0b'} />
          <PlaylistGrid />
          <ArtistSocialMedia artist={selectedArtist} />
        </div>
      </section>

    </div>
  );
};

export default ArtistsPage;
